import { jsx } from '@emotion/react';
import { useState, useMemo, useCallback, useEffect } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { Button, ButtonSize } from '@shipengine/giger';
import { PaymentMethod } from '@packlink/packlink-sdk';
import { useTranslation } from '@packlink/translation-provider';
import { CreditCardFieldType, CreditCardField } from './CreditCardField.js';
import { getCreditCardFormStyles, getCreditCardFormGroup, getCreditCardExpirationStyles, getCreditCardCvvStyles } from './CreditCardStyles.js';
import { jsxs, Fragment, jsx as jsx$1 } from '@emotion/react/jsx-runtime';
import { tokenize, initHostedFields } from '../../../utils/braintree.js';
import { LoggerPaymentErrorTagValues } from '../../../types/types.js';

var BREADCRUMBS = /*#__PURE__*/function (BREADCRUMBS) {
  BREADCRUMBS["SETUP_SUCCESS"] = "Hosted fields setup success";
  BREADCRUMBS["SETUP_ERROR"] = "Hosted fields setup error";
  return BREADCRUMBS;
}(BREADCRUMBS || {});
const CreditCard = _ref => {
  let {
    buttonText,
    cdnUrl,
    clientBraintree,
    hints,
    isLoading = false,
    isPaymentBlocked = false,
    placeholders,
    spinner,
    wrapperComponentTag = 'form',
    onBlur,
    onCheckForExternalError,
    onPay,
    onSetupError,
    onSetupSuccess,
    onVerificationStart,
    onVerificationError
  } = _ref;
  const {
    t
  } = useTranslation();
  const [isReady, setIsReady] = useState(false);
  const [loading, setLoading] = useState(!!isLoading);
  const [cardType, setCardType] = useState();
  const [hostedFieldsInstance, setHostedFieldsInstance] = useState();
  const [fieldsState, setFieldsState] = useState({
    number: {
      isEmpty: true,
      isFocused: false,
      isPotentiallyValid: true,
      isValid: false,
      touched: false
    },
    expirationDate: {
      isEmpty: true,
      isFocused: false,
      isPotentiallyValid: true,
      isValid: false,
      touched: false
    },
    cvv: {
      isEmpty: true,
      isFocused: false,
      isPotentiallyValid: true,
      isValid: false,
      touched: false
    }
  });
  const labels = useMemo(() => ({
    number: t('form.label.credit-number'),
    expirationDate: t('form.label.expiration-date'),
    cvv: t('form.label.cvv')
  }), [t]);
  const errors = useMemo(() => ({
    number: t('form.error.format', {
      field: t('form.label.credit-number')
    }),
    expirationDate: t('form.error.format', {
      field: t('form.label.expiration-date')
    }),
    cvv: t('form.error.format', {
      field: t('form.label.cvv')
    })
  }), [t]);
  const tokenizePayment = useCallback(mustSetFieldsState => {
    if (!hostedFieldsInstance) {
      return;
    }
    if (isPaymentBlocked) {
      onCheckForExternalError === null || onCheckForExternalError === void 0 ? void 0 : onCheckForExternalError();
      return;
    }
    if (mustSetFieldsState) {
      setFieldsState({
        number: {
          ...fieldsState.number,
          touched: true
        },
        expirationDate: {
          ...fieldsState.expirationDate,
          touched: true
        },
        cvv: {
          ...fieldsState.cvv,
          touched: true
        }
      });
    }
    onVerificationStart();
    tokenize(hostedFieldsInstance).then(payload => {
      const {
        nonce,
        details: {
          bin
        }
      } = payload;
      onPay({
        nonce,
        bin
      });
    }).catch(onVerificationError);
  }, [fieldsState.number, fieldsState.expirationDate, fieldsState.cvv, hostedFieldsInstance, onPay, onVerificationError, onVerificationStart]);
  const setCardTypeState = useCallback(event => {
    setCardType(event.cards.length === 1 ? event.cards[0].type : undefined);
  }, [setCardType]);
  const setHostedFieldsState = useCallback(event => {
    setFieldsState(oldState => ({
      number: {
        ...event.fields.number,
        touched: oldState.number.touched || event.emittedBy === 'number'
      },
      cvv: {
        ...event.fields.cvv,
        touched: oldState.cvv.touched || event.emittedBy === 'cvv'
      },
      expirationDate: {
        ...event.fields.expirationDate,
        touched: oldState.expirationDate.touched || event.emittedBy === 'expirationDate'
      }
    }));
  }, []);
  const handleHostedFieldsInputSubmitRequestHandler = useCallback(event => {
    setHostedFieldsState(event);
    tokenizePayment(false);
  }, [setHostedFieldsState, tokenizePayment]);
  const hostedFieldsHandler = useCallback(customHandler => event => {
    customHandler === null || customHandler === void 0 ? void 0 : customHandler(event);
    setHostedFieldsState(event);
  }, [setHostedFieldsState]);
  const onFormSubmitHandler = useCallback(event => {
    event.preventDefault();
    tokenizePayment(true);
  }, [tokenizePayment]);
  const handleSubmit = useCallback(event => {
    if (wrapperComponentTag !== 'form') {
      onFormSubmitHandler(event);
    }
  }, [onFormSubmitHandler, wrapperComponentTag]);
  useEffect(() => {
    setLoading(!!isLoading);
  }, [isLoading]);
  useEffectOnce(() => {
    const initializeHostedFields = async client => {
      try {
        const hostedFields = await initHostedFields(client, {
          [CreditCardFieldType.NUMBER]: {
            placeholder: placeholders ? placeholders[CreditCardFieldType.NUMBER] : '',
            selector: `#braintree-${CreditCardFieldType.NUMBER}`
          },
          [CreditCardFieldType.CVV]: {
            placeholder: placeholders ? placeholders[CreditCardFieldType.CVV] : '',
            selector: `#braintree-${CreditCardFieldType.CVV}`
          },
          [CreditCardFieldType.EXPIRATION_DATE]: {
            placeholder: placeholders ? placeholders[CreditCardFieldType.EXPIRATION_DATE] : '',
            selector: `#braintree-${CreditCardFieldType.EXPIRATION_DATE}`
          }
        });
        hostedFields.on('blur', hostedFieldsHandler(onBlur));
        hostedFields.on('cardTypeChange', hostedFieldsHandler(setCardTypeState));
        hostedFields.on('empty', hostedFieldsHandler());
        hostedFields.on('focus', hostedFieldsHandler());
        hostedFields.on('notEmpty', hostedFieldsHandler());
        hostedFields.on('validityChange', hostedFieldsHandler());
        hostedFields.on('inputSubmitRequest', handleHostedFieldsInputSubmitRequestHandler);
        setHostedFieldsInstance(hostedFields);
        onSetupSuccess(BREADCRUMBS.SETUP_SUCCESS);
        setIsReady(true);
      } catch (error) {
        onSetupError(error, BREADCRUMBS.SETUP_ERROR, LoggerPaymentErrorTagValues.PAYMENT_CREDIT_CARD_SETUP, PaymentMethod.CREDITCARD3DSECURE);
        setIsReady(false);
      }
    };
    initializeHostedFields(clientBraintree);
  });
  const renderChildren = () => {
    return jsxs(Fragment, {
      children: [jsx$1(CreditCardField, {
        cardType: cardType,
        cdnUrl: cdnUrl,
        error: errors.number,
        hint: hints === null || hints === void 0 ? void 0 : hints.number,
        label: labels.number,
        type: CreditCardFieldType.NUMBER,
        ...fieldsState.number
      }), jsxs("div", {
        css: getCreditCardFormGroup,
        children: [jsx$1(CreditCardField, {
          css: getCreditCardExpirationStyles,
          error: errors.expirationDate,
          hint: hints === null || hints === void 0 ? void 0 : hints.expirationDate,
          label: labels.expirationDate,
          type: CreditCardFieldType.EXPIRATION_DATE,
          ...fieldsState.expirationDate
        }), jsx$1(CreditCardField, {
          css: getCreditCardCvvStyles,
          error: errors.cvv,
          hint: hints === null || hints === void 0 ? void 0 : hints.cvv,
          label: labels.cvv,
          type: CreditCardFieldType.CVV,
          ...fieldsState.cvv
        })]
      }), jsx$1(Button, {
        isFullWidth: true,
        isLoading: loading || !isReady,
        size: ButtonSize.LARGE,
        spinner: spinner,
        type: wrapperComponentTag === 'form' ? 'submit' : 'button',
        onClick: handleSubmit,
        children: buttonText
      })]
    });
  };
  return jsx(wrapperComponentTag, {
    id: 'credit-card-form',
    css: getCreditCardFormStyles,
    onSubmit: onFormSubmitHandler
  }, renderChildren());
};

export { CreditCard };
//# sourceMappingURL=CreditCard.js.map
